// frontend/src/components/Orders.js
import React, { useState, useEffect, useRef } from 'react';
import { Trash2, Edit, Plus, Table as TableIcon, LayoutDashboard, Calendar, X, Volume2, VolumeX, Clock } from 'lucide-react';
import axios from 'axios';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { buttonPrimary, buttonSecondary, buttonDanger, badgePrimary, focusRing, textPrimary, bgPrimary, swalConfirmColor, swalCancelColor } from '../theme';

const API_URL = '/api';

const Orders = ({ user }) => {
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [viewMode, setViewMode] = useState('kanban');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval] = useState(5000);
  const [showFilters, setShowFilters] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [volume, setVolume] = useState(0.5);
  const [newOrderIds, setNewOrderIds] = useState(new Set());
  const previousOrderCountRef = useRef(0);
  const audioContextRef = useRef(null);
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    table_number: '',
    order_details: '',
    paid: 'No',
    status: 'Order Placed'
  });
  const [checklistItems, setChecklistItems] = useState([]);
  const [isEditingText, setIsEditingText] = useState(false);

  const statuses = ['Order Placed', 'In Preparation', 'Served'];
  const paidOptions = ['Yes', 'No'];
  const isAdmin = user.role === 'admin';

  // Initialize Web Audio API
  useEffect(() => {
    audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  // Play notification sound
  const playNotificationSound = () => {
    if (!soundEnabled || !audioContextRef.current) return;

    const ctx = audioContextRef.current;
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);

    oscillator.frequency.value = 800;
    oscillator.type = 'sine';
    gainNode.gain.setValueAtTime(volume, ctx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);

    oscillator.start(ctx.currentTime);
    oscillator.stop(ctx.currentTime + 0.5);

    // Second beep
    setTimeout(() => {
      const osc2 = ctx.createOscillator();
      const gain2 = ctx.createGain();
      osc2.connect(gain2);
      gain2.connect(ctx.destination);
      osc2.frequency.value = 1000;
      osc2.type = 'sine';
      gain2.gain.setValueAtTime(volume, ctx.currentTime);
      gain2.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.5);
      osc2.start(ctx.currentTime);
      osc2.stop(ctx.currentTime + 0.5);
    }, 200);
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      fetchOrders();
    }, refreshInterval);

    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval]);

  useEffect(() => {
    if (orders.length > 0) {
      const filtered = orders.filter(order => {
        const orderDate = new Date(order.date).toISOString().split('T')[0];
        return orderDate === selectedDate;
      });
      setFilteredOrders(filtered);
    }
  }, [orders, selectedDate]);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API_URL}/orders`);
      const newOrders = response.data;
      
      // Check for new orders
      if (previousOrderCountRef.current > 0 && newOrders.length > previousOrderCountRef.current) {
        const newOrderCount = newOrders.length - previousOrderCountRef.current;
        playNotificationSound();
        toast.success(`🔔 ${newOrderCount} New Order${newOrderCount > 1 ? 's' : ''}!`, {
          autoClose: 5000,
          style: { fontSize: '18px', fontWeight: 'bold' }
        });
        
        // Mark new orders for animation
        const existingIds = new Set(orders.map(o => o.id));
        const newIds = new Set(newOrders.filter(o => !existingIds.has(o.id)).map(o => o.id));
        setNewOrderIds(newIds);
        
        // Clear new order animations after 10 seconds
        setTimeout(() => setNewOrderIds(new Set()), 10000);
      }
      
      previousOrderCountRef.current = newOrders.length;
      setOrders(newOrders);
    } catch (err) {
      console.error('Error fetching orders:', err);
      toast.error('Failed to fetch orders');
    }
  };

  const handleSubmit = async () => {
    if (!formData.date) {
      toast.warning('Please fill in all required fields');
      return;
    }

    // For editing orders with checklist, save as JSON
    let orderDetails = formData.order_details;
    if (editingOrder && checklistItems.length > 0 && !isEditingText) {
      // Save as JSON to preserve checkbox states
      orderDetails = JSON.stringify(checklistItems);
    } else if (!orderDetails) {
      toast.warning('Please add order details');
      return;
    }

    try {
      setLoading(true);
      const submitData = { ...formData, order_details: orderDetails };
      
      if (editingOrder) {
        await axios.put(`${API_URL}/orders/${editingOrder.id}`, submitData);
        toast.success('Order updated successfully!');
      } else {
        await axios.post(`${API_URL}/orders`, submitData);
        toast.success('Order added successfully!');
      }
      await fetchOrders();
      resetForm();
    } catch (err) {
      console.error('Error saving order:', err);
      toast.error(err.response?.data?.message || 'Failed to save order');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Delete Order?',
      text: "This action cannot be undone",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: swalConfirmColor,
      cancelButtonColor: swalCancelColor,
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
      reverseButtons: true,
      customClass: {
        popup: 'rounded-lg',
        confirmButton: 'px-6 py-3 rounded-md font-medium text-lg',
        cancelButton: 'px-6 py-3 rounded-md font-medium text-lg'
      }
    });

    if (result.isConfirmed) {
      try {
        setLoading(true);
        await axios.delete(`${API_URL}/orders/${id}`);
        await fetchOrders();
        resetForm();
        toast.success('Order deleted successfully!');
      } catch (err) {
        console.error('Error deleting order:', err);
        toast.error('Failed to delete order');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleEdit = (order) => {
    setEditingOrder(order);
    setFormData({
      date: order.date.split('T')[0],
      table_number: order.table_number || '',
      order_details: order.order_details,
      paid: order.paid,
      status: order.status
    });
    
    // Try to parse as JSON first (new format with checkbox states)
    try {
      const parsed = JSON.parse(order.order_details);
      if (Array.isArray(parsed)) {
        setChecklistItems(parsed.map(item => ({
          ...item,
          id: item.id || Math.random().toString(36).substr(2, 9)
        })));
        setIsEditingText(false);
        setShowForm(true);
        return;
      }
    } catch (e) {
      // Not JSON, parse as HTML/text
    }
    
    // Parse HTML/text into checklist items (legacy format)
    const parser = new DOMParser();
    const doc = parser.parseFromString(order.order_details, 'text/html');
    const text = doc.body.textContent || '';
    
    // Split by comma, newline, or HTML line breaks
    const items = text
      .split(/[,\n]|<br>|<\/li>|<\/p>/)
      .map(item => item.trim())
      .filter(item => item.length > 0)
      .map(item => ({
        text: item,
        checked: false,
        id: Math.random().toString(36).substr(2, 9)
      }));
    
    setChecklistItems(items);
    setIsEditingText(false);
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      date: new Date().toISOString().split('T')[0],
      table_number: '',
      order_details: '',
      paid: 'No',
      status: 'Order Placed'
    });
    setEditingOrder(null);
    setChecklistItems([]);
    setIsEditingText(false);
    setShowForm(false);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleDateChange = (newDate) => {
    setSelectedDate(newDate);
  };

  const goToToday = () => {
    setSelectedDate(new Date().toISOString().split('T')[0]);
    toast.info('Showing today\'s orders');
  };

  const toggleChecklistItem = (itemId) => {
    setChecklistItems(checklistItems.map(item => 
      item.id === itemId ? { ...item, checked: !item.checked } : item
    ));
  };

  const updateChecklistItemText = (itemId, newText) => {
    setChecklistItems(checklistItems.map(item => 
      item.id === itemId ? { ...item, text: newText } : item
    ));
  };

  const deleteChecklistItem = (itemId) => {
    setChecklistItems(checklistItems.filter(item => item.id !== itemId));
  };

  const addChecklistItem = () => {
    setChecklistItems([...checklistItems, {
      text: '',
      checked: false,
      id: Math.random().toString(36).substr(2, 9)
    }]);
  };

  const switchToTextEdit = () => {
    // Convert checklist to plain text for editing with commas
    const plainText = checklistItems.map(item => item.text).join(', ');
    setFormData({...formData, order_details: plainText});
    setIsEditingText(true);
  };

  const switchToChecklist = () => {
    // Parse text back into checklist, stripping HTML tags
    const parser = new DOMParser();
    const doc = parser.parseFromString(formData.order_details, 'text/html');
    const text = doc.body.textContent || '';
    
    const items = text
      .split(/[,\n]/)
      .map(item => item.trim())
      .filter(item => item.length > 0)
      .map(item => ({
        text: item,
        checked: false,
        id: Math.random().toString(36).substr(2, 9)
      }));
    setChecklistItems(items);
    setIsEditingText(false);
  };

  const toggleAutoRefresh = () => {
    setAutoRefresh(!autoRefresh);
    if (!autoRefresh) {
      toast.info('Auto-refresh enabled');
    } else {
      toast.info('Auto-refresh disabled');
    }
  };

  const toggleSound = () => {
    setSoundEnabled(!soundEnabled);
    if (!soundEnabled) {
      toast.info('🔔 Sound notifications enabled');
      playNotificationSound();
    } else {
      toast.info('🔕 Sound notifications disabled');
    }
  };

  const getTimeElapsed = (createdAt) => {
    const now = new Date();
    const created = new Date(createdAt);
    const diffMs = now - created;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m`;
    const hours = Math.floor(diffMins / 60);
    const mins = diffMins % 60;
    return `${hours}h ${mins}m`;
  };

  const getAgeColor = (createdAt, status) => {
    if (status === 'Served') return 'text-gray-500';
    
    const now = new Date();
    const created = new Date(createdAt);
    const diffMins = Math.floor((now - created) / 60000);
    
    if (diffMins < 10) return 'text-green-600';
    if (diffMins < 20) return 'text-yellow-600';
    return 'text-red-600 font-bold';
  };

  const KanbanView = () => {
    const ordersByStatus = statuses.reduce((acc, status) => {
      acc[status] = filteredOrders.filter(order => order.status === status);
      return acc;
    }, {});

    const formatOrderDetails = (orderDetails) => {
      try {
        const parsed = JSON.parse(orderDetails);
        if (Array.isArray(parsed)) {
          return (
            <div className="space-y-1">
              {parsed.map((item, idx) => (
                <div key={idx} className="flex items-start gap-2 text-sm">
                  <span className={item.checked ? 'text-gray-400 line-through' : 'text-gray-700'}>
                    {item.checked ? '✓' : '•'} {item.text}
                  </span>
                </div>
              ))}
            </div>
          );
        }
      } catch (e) {
        // Not JSON
      }
      return <div className="text-sm" dangerouslySetInnerHTML={{ __html: orderDetails }} />;
    };

    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {statuses.map(status => (
          <div key={status} className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-bold text-lg mb-4 text-gray-700 flex items-center justify-between">
              {status}
              <span className={`text-sm text-white rounded-full px-2 py-1 ${bgPrimary}`}>
                {ordersByStatus[status].length}
              </span>
            </h3>
            <div className="space-y-3">
              {ordersByStatus[status].map(order => {
                const isNew = newOrderIds.has(order.id);
                const showTimer = status === 'Order Placed' || status === 'In Preparation';
                
                return (
                  <div 
                    key={order.id} 
                    onClick={() => handleEdit(order)}
                    className={`bg-white rounded-lg shadow p-4 border-2 transition-all cursor-pointer hover:shadow-lg hover:scale-105 ${
                      isNew ? 'border-red-500 animate-pulse' : 'border-transparent'
                    }`}
                  >
                    {isNew && (
                      <div className="mb-2 bg-red-500 text-white px-3 py-1 rounded-md font-bold text-sm text-center animate-pulse">
                        🔔 NEW ORDER!
                      </div>
                    )}
                    
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex flex-col">
                        <span className="text-sm text-gray-500">{formatDate(order.date)}</span>
                        {order.table_number && (
                          <span className="text-sm font-semibold text-blue-600 mt-1">
                            Table {order.table_number}
                          </span>
                        )}
                      </div>
                      {showTimer && (
                        <div className={`flex flex-col items-end ${getAgeColor(order.created_at, order.status)}`}>
                          <Clock size={16} className="mb-1" />
                          <span className="text-sm font-bold">{getTimeElapsed(order.created_at)}</span>
                        </div>
                      )}
                    </div>
                    
                    <div className="mb-3">
                      {formatOrderDetails(order.order_details)}
                    </div>
                    
                    <div className="pt-2 border-t text-xs text-gray-500 flex items-center gap-1">
                      <Edit size={12} />
                      Tap to edit
                    </div>
                  </div>
                );
              })}
              {ordersByStatus[status].length === 0 && (
                <div className="text-center text-gray-400 py-8 text-sm">
                  No orders
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const TableView = () => {
    const formatOrderDetails = (orderDetails) => {
      try {
        const parsed = JSON.parse(orderDetails);
        if (Array.isArray(parsed)) {
          return (
            <div className="space-y-1">
              {parsed.map((item, idx) => (
                <div key={idx} className="flex items-start gap-2 text-sm">
                  <span className={item.checked ? 'text-gray-400 line-through' : 'text-gray-700'}>
                    {item.checked ? '✓' : '•'} {item.text}
                  </span>
                </div>
              ))}
            </div>
          );
        }
      } catch (e) {
        // Not JSON
      }
      return <div className="text-sm" dangerouslySetInnerHTML={{ __html: orderDetails }} />;
    };

    return (
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {filteredOrders.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            No orders for {formatDate(selectedDate)}. Click "Add New Order" to get started!
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Date</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Table</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Time</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Order Details</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Status</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredOrders.map((order) => {
                  const isNew = newOrderIds.has(order.id);
                  const showTimer = order.status === 'Order Placed' || order.status === 'In Preparation';
                  
                  return (
                    <tr key={order.id} className={`hover:bg-gray-50 transition-colors ${isNew ? 'bg-red-50 animate-pulse' : ''}`}>
                      <td className="px-4 py-3 text-sm text-gray-700">{formatDate(order.date)}</td>
                      <td className="px-4 py-3 text-sm font-semibold text-blue-600">
                        {order.table_number || '-'}
                      </td>
                      <td className="px-4 py-3">
                        {showTimer && (
                          <div className={`flex items-center gap-2 ${getAgeColor(order.created_at, order.status)}`}>
                            <Clock size={16} />
                            <span className="text-sm font-bold">{getTimeElapsed(order.created_at)}</span>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {isNew && (
                          <span className="bg-red-500 text-white px-2 py-0.5 rounded text-xs font-bold mr-2">NEW!</span>
                        )}
                        <div className="max-w-md">
                          {formatOrderDetails(order.order_details)}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          order.status === 'Served' ? 'bg-green-100 text-green-800' :
                          order.status === 'In Preparation' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-blue-100 text-blue-800'
                        }`}>
                          {order.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEdit(order)}
                            className="text-blue-600 hover:text-blue-800"
                          >
                            <Edit size={18} />
                          </button>
                          {isAdmin && (
                            <button
                              onClick={() => handleDelete(order.id)}
                              className="text-red-600 hover:text-red-800"
                            >
                              <Trash2 size={18} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <div className="flex gap-2">
          <button
            onClick={() => setShowForm(!showForm)}
            className={`flex items-center gap-2 px-4 py-2 rounded-md ${buttonPrimary}`}
          >
            <Plus size={18} />
            Add New Order
          </button>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
          >
            <Calendar size={18} />
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </button>
        </div>
        
        <div className="flex bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => setViewMode('table')}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-md ${
              viewMode === 'table' ? 'bg-white shadow' : 'text-gray-600'
            }`}
          >
            <TableIcon size={18} />
            Table
          </button>
          <button
            onClick={() => setViewMode('kanban')}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-md ${
              viewMode === 'kanban' ? 'bg-white shadow' : 'text-gray-600'
            }`}
          >
            <LayoutDashboard size={18} />
            Kanban
          </button>
        </div>
      </div>

      {showFilters && (
        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <div className="flex items-center gap-4">
            <Calendar size={20} className="text-gray-500" />
            <div className="flex items-center gap-2 flex-1">
              <label className="text-sm font-medium text-gray-700">Filter by Date:</label>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => handleDateChange(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
              <button
                onClick={goToToday}
                className={`px-4 py-2 rounded-md text-sm font-medium ${badgePrimary}`}
              >
                Today
              </button>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-sm text-gray-600">
                Showing <span className={`font-semibold ${textPrimary}`}>{filteredOrders.length}</span> order{filteredOrders.length !== 1 ? 's' : ''}
              </div>
              <div className="flex items-center gap-2 border-l pl-3">
                <button
                  onClick={toggleAutoRefresh}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium ${
                    autoRefresh 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {autoRefresh ? '● Auto-refresh ON' : '○ Auto-refresh OFF'}
                </button>
                <button
                  onClick={toggleSound}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium flex items-center gap-2 ${
                    soundEnabled 
                      ? 'bg-blue-100 text-blue-800' 
                      : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {soundEnabled ? <Volume2 size={18} /> : <VolumeX size={18} />}
                  Sound
                </button>
              </div>
            </div>
          </div>
          
          {soundEnabled && (
            <div className="mt-4 flex items-center gap-4 bg-blue-50 p-4 rounded-lg">
              <Volume2 size={20} className="text-blue-600" />
              <span className="text-sm font-medium text-blue-800">Volume:</span>
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={volume}
                onChange={(e) => setVolume(parseFloat(e.target.value))}
                className="flex-1"
              />
              <span className="text-sm font-bold text-blue-800 w-12">{Math.round(volume * 100)}%</span>
            </div>
          )}
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold">
                {editingOrder ? 'Edit Order' : 'Add New Order'}
              </h2>
              <button
                onClick={resetForm}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={24} />
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="grid grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({...formData, date: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Table #</label>
                  <input
                    type="text"
                    value={formData.table_number}
                    onChange={(e) => setFormData({...formData, table_number: e.target.value})}
                    placeholder="e.g. 5, A1"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Paid</label>
                  <select
                    value={formData.paid}
                    onChange={(e) => setFormData({...formData, paid: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  >
                    {paidOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({...formData, status: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  >
                    {statuses.map(status => (
                      <option key={status} value={status}>{status}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Order Details
                  {editingOrder && (
                    <button
                      type="button"
                      onClick={isEditingText ? switchToChecklist : switchToTextEdit}
                      className="ml-3 text-xs text-blue-600 hover:text-blue-800 font-medium"
                    >
                      {isEditingText ? '← Switch to Checklist' : 'Edit as Text →'}
                    </button>
                  )}
                </label>
                {editingOrder && !isEditingText ? (
                  <div className="border-2 border-gray-300 rounded-md p-4 bg-gray-50 max-h-96 overflow-y-auto">
                    {checklistItems.length === 0 ? (
                      <p className="text-gray-500 text-sm">No items found</p>
                    ) : (
                      <div className="space-y-2">
                        {checklistItems.map((item) => (
                          <div 
                            key={item.id} 
                            className="flex items-start gap-3 p-3 bg-white rounded-lg border border-gray-200 hover:border-orange-300 transition-colors group"
                          >
                            <div className="flex items-center h-6">
                              <input
                                type="checkbox"
                                checked={item.checked}
                                onChange={() => toggleChecklistItem(item.id)}
                                className="w-5 h-5 text-orange-600 bg-gray-100 border-gray-300 rounded focus:ring-orange-500 cursor-pointer"
                              />
                            </div>
                            <input
                              type="text"
                              value={item.text}
                              onChange={(e) => updateChecklistItemText(item.id, e.target.value)}
                              className={`flex-1 text-base bg-transparent border-none focus:outline-none focus:ring-0 ${
                                item.checked ? 'line-through text-gray-400' : 'text-gray-800 font-medium'
                              }`}
                              placeholder="Item name"
                            />
                            <button
                              type="button"
                              onClick={() => deleteChecklistItem(item.id)}
                              className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700 transition-opacity p-1"
                            >
                              <X size={16} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={addChecklistItem}
                      className="mt-3 w-full px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-orange-400 hover:text-orange-600 transition-colors text-sm font-medium"
                    >
                      + Add Item
                    </button>
                    <div className="mt-4 pt-4 border-t border-gray-200 text-sm text-gray-600">
                      <strong>Progress:</strong> {checklistItems.filter(i => i.checked).length} of {checklistItems.length} items completed
                    </div>
                  </div>
                ) : (
                  <ReactQuill
                    theme="snow"
                    value={formData.order_details}
                    onChange={(value) => setFormData({...formData, order_details: value})}
                    className="bg-white"
                    style={{ height: '200px', marginBottom: '50px' }}
                    placeholder="Enter items separated by commas or new lines (e.g., Burger, Fries, Coke)"
                  />
                )}
              </div>

              <div className="flex flex-col gap-3 pt-4">
                <button
                  onClick={handleSubmit}
                  disabled={loading}
                  className={`w-full px-6 py-3 rounded-md font-medium disabled:bg-gray-400 ${buttonPrimary}`}
                >
                  {loading ? 'Saving...' : (editingOrder ? 'Update' : 'Add') + ' Order'}
                </button>
                
                <div className="flex gap-3">
                  {editingOrder && isAdmin && (
                    <button
                      onClick={() => handleDelete(editingOrder.id)}
                      disabled={loading}
                      className={`flex-1 px-6 py-3 rounded-md font-medium disabled:bg-gray-400 flex items-center justify-center gap-2 ${buttonDanger}`}
                    >
                      <Trash2 size={18} />
                      Delete Order
                    </button>
                  )}
                  <button
                    onClick={resetForm}
                    className={`${editingOrder && isAdmin ? 'flex-1' : 'w-full'} px-6 py-3 rounded-md font-medium ${buttonSecondary}`}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {viewMode === 'table' ? <TableView /> : <KanbanView />}
    </div>
  );
};

export default Orders;
